import { LitElement, css, html, unsafeCSS, nothing } from 'lit';
import styles from './input.scss?inline';
import { ATTRIBUTES } from '../constants';
import { ref, createRef } from 'lit/directives/ref.js';
import { InputStyleController } from '@/controllers/InputStyleController';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { classMap } from 'lit/directives/class-map.js';
import debounce from 'lodash/debounce';

export default class SotbitInputStyle extends LitElement {
    static styles = css`
        ${unsafeCSS(styles)}
    `;

    static properties = {
        label: { type: String },
        clearable: { type: Boolean },
        helperText: { type: String, attribute: ATTRIBUTES.HELPER_TEXT },
        errorText: { type: String, attribute: ATTRIBUTES.ERROR_TEXT },
        error: { type: Boolean },
        focused: { type: Boolean, state: true },
        smallLabel: { type: Boolean, state: true },
        defaultSlotPosition: {
            type: String,
            attribute: 'default-slot-position',
        },
        disabled: { type: Boolean, state: true },
        readonly: { type: Boolean, state: true },
        required: { type: Boolean, state: true },
        placeholderShow: { type: Boolean, state: true },
        watchAttrs: { type: String },
        noReadonlyStyle: { type: Boolean, attribute: 'no-readonly-style' },
        multilineForTextarea: { type: Boolean, reflect: true },
    };

    constructor() {
        super();

        this.label = null;
        this.helperText = null;
        this.errorText = null;
        this.error = false;
        this.input = null;
        this.slotRef = createRef();
        this.inputContainerSlot = createRef();
        this.focused = false;
        this.smallLabel = false;
        this.placeholderShow = true;
        this.defaultSlotPosition = null;
        this.disabled = false;
        this.readonly = false;
        this.noReadonlyStyle = false;
        this.watchAttrs = null;
        this.required = false;
        this.helperInputs = [];

        this._controller = new InputStyleController(this, {
            getInput: () => {
                return this.input;
            },
        });

        this._controller.inputObserverConfig?.attributeFilter?.push('error');
        this._controller.inputObserverConfig?.attributeFilter?.push('error-text');
    }

    get value() {
        return this.input?.value;
    }

    set value(value) {
        if (this.input) {
            this.input.value = value;
        }
    }

    get form() {
        return this.input?.form;
    }

    _initControllers() {
        this._destroyControllers();

        if (!this.input) {
            return;
        }

        this._controller?.inputObserver?.observe(this.input, this._controller.inputObserverConfig);
        this._controller?.calcInputAttr();
        this._controller?.addHandlers();
    }

    _destroyControllers() {
        this._controller?.removeHandlers();
        this._controller?.inputObserver?.disconnect();
    }

    getInputContainer() {
        return this.inputContainerSlot?.value;
    }

    connectedCallback() {
        super.connectedCallback();

        this.addEventListener('focusin', this._handleFocus);
        this.addEventListener('focusout', this._handleBlur);
        this.addEventListener('input', this._handleInputInput);

        if (this.input) {
            this._destroyControllers();
            this._initControllers();

            this.calculatePlaceholderShow();
            this.calculateSmallLabel();
        }
    }

    disconnectedCallback() {
        super.disconnectedCallback();

        this.removeEventListener('focusin', this._handleFocus);
        this.removeEventListener('focusout', this._handleBlur);
        this.removeEventListener('input', this._handleInputInput);

        this._destroyControllers();
    }

    calculatePlaceholderShow() {
        this.placeholderShow = this.input?.value?.length === 0 && this.input?.placeholder?.length > 0;
    }

    isAutofilled(input) {
        return (
            input.matches(':-webkit-autofill') ||
            input.matches(':autofill') ||
            window.getComputedStyle(input).backgroundColor !== 'rgba(0, 0, 0, 0)'
        );
    }

    autofillHandler = (e) => {
        // This is a hack for input autofill. Don't change this name or anything else.
        if (e.propertyName === 'transform') {
            this.smallLabel = this.isAutofilled(this.input);

            if (!this.smallLabel) {
                this.calculateSmallLabel();
            }
        }
    };

    calculateSmallLabel() {
        const check = (input) => {
            return input?.value?.length > 0 || this.placeholderShow;
        };

        this.smallLabel = check(this.input);

        if (!this.smallLabel && this.helperInputs) {
            for (const helpInput of this.helperInputs) {
                this.smallLabel = check(helpInput);

                if (this.smallLabel) {
                    return;
                }
            }
        }
    }

    clearInput() {
        if (!this.input || this.input.hasAttribute('disabled') || this.input.hasAttribute('readonly')) {
            return;
        }

        this.input.value = '';
        this.dispatchEvent(new Event('input', { bubbles: true, composed: true }));

        setTimeout(() => {
            this?.input.focus();
        }, 0);
    }

    _handleSlotChange() {
        this._slotChangeDebounce();
    }

    _slotChangeDebounce = debounce(() => {
        this._updateSlotState(ATTRIBUTES.HAS_SLOT_ICON);
    }, 60);

    _updateSlotState(attr) {
        if (!this.slotRef.value) {
            return;
        }

        const hasContent = this.slotRef.value?.assignedElements().length > 0;

        if (hasContent) {
            this.setAttribute(attr, '');
        } else {
            this.removeAttribute(attr);
        }
    }

    _handleFocus = () => {
        this.focused = !this.input?.hasAttribute('disabled') && !this.input?.hasAttribute('readonly');
        if (this.focused) {
            this.smallLabel = true;
        }

        this.focus();
    };

    _handleBlur = () => {
        this.focused = false;
        this.calculatePlaceholderShow();
        this.calculateSmallLabel();
    };

    _handleInputInput = () => {
        this.calculatePlaceholderShow();
    };

    _handleInputSlotChangeDebounce = debounce((currentTarget) => {
        const inputSlotElements = currentTarget?.assignedElements({
            flatten: true,
        });
        this.input?.removeEventListener('transitionstart', this.autofillHandler);
        this.input = inputSlotElements[0];
        this.helperInputs = inputSlotElements.slice(1);

        this._initControllers();

        this.calculatePlaceholderShow();
        this.calculateSmallLabel();
        // This is a hack for input autofill. Don't change this name or anything else.
        this.input?.addEventListener('transitionstart', this.autofillHandler);
        this.multilineForTextarea = this.input?.localName === 'textarea';
    });

    _handleInputSlotChange(e) {
        this._handleInputSlotChangeDebounce(e.currentTarget);
    }

    getInputContainerClasses() {
        return {
            'input-container': true,
            focused: this.focused,
            disabled: this.disabled,
            readonly: this.readonly && !this.noReadonlyStyle,
            placeholder: this.placeholderShow,
        };
    }

    getLabelClasses() {
        return {
            label: true,
            small: this.smallLabel,
            required: this.required,
        };
    }

    render() {
        return html`
            <div part="input-field" class="input-field" @click="${() => this.input?.focus()}">
                <div
                    class="${classMap(this.getInputContainerClasses())}"
                    ${ref(this.inputContainerSlot)}
                    part="input-container"
                >
                    <div part="outline" class="outline"></div>
                    <slot name="icon-left"></slot>
                    <div class="input-wrapper" part="input-wrapper">
                        <slot name="input" @slotchange="${this._handleInputSlotChange}"></slot>
                        ${this.label && this.label.length > 0
                            ? html` <label class="${classMap(this.getLabelClasses())}"
                                  >${unsafeHTML(this.label)}</label
                              >`
                            : nothing}
                    </div>
                    ${this.clearable
                        ? html`<div
                              class="clear-button-wrapper"
                              @mousedown="${this.clearInput}"
                              part="clear-button-wrapper"
                          >
                              <slot name="clear-button">
                                  <sotbit-icon
                                      part="clear-button"
                                      icon="close"
                                      size="m"
                                      variant="secondary"
                                      pointer
                                  ></sotbit-icon>
                              </slot>
                          </div>`
                        : ''}

                    <div class="icon-container" part="icon-container">
                        <slot name="icon-right" ${ref(this.slotRef)} @slotchange="${this._handleSlotChange}"></slot>
                    </div>
                </div>
                ${this.defaultSlotPosition === 'top' ? html` <slot></slot>` : ''}
                <span part="helper-text" class="helper-text"
                    >${this.error && this.errorText ? this.errorText : this.helperText}</span
                >
                ${this.defaultSlotPosition === 'bottom' ? html` <slot></slot>` : ''}
            </div>
        `;
    }
}
